/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { US_STATE_CODES } from "./usStateCodes.js";

// =====================================================================
// mapsService  ::  whitelisted CRUD over RLS
// Every write filters caller-supplied keys through an explicit ALLOWED set
// before touching .update() / .insert(). DB-controlled columns (id, owner_id,
// created_at, updated_at, published_at) are never accepted from the client.
// All filtering on auth.uid() happens via RLS.
// =====================================================================

const HEX_RE  = /^#[0-9a-fA-F]{6}$/;
const HTTP_RE = /^https?:\/\//i;
const STATE_CODE_SET = new Set(US_STATE_CODES);

function pick(obj, allowed) {
  const out = {};
  for (const key of allowed) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) out[key] = obj[key];
  }
  return out;
}

function validateMapSettings(patch) {
  const errors = [];
  if (patch.name != null && (typeof patch.name !== "string" || patch.name.length < 1 || patch.name.length > 120)) {
    errors.push("Name must be 1–120 chars.");
  }
  if (patch.map_title != null && typeof patch.map_title === "string" && patch.map_title.length > 200) {
    errors.push("Title too long.");
  }
  for (const k of ["map_title_color", "map_bg_color", "map_label_color", "map_unassigned_color", "map_legend_text_color"]) {
    if (patch[k] != null && !HEX_RE.test(patch[k])) errors.push(`${k} must be #RRGGBB.`);
  }
  if (patch.map_bg_transparent != null && typeof patch.map_bg_transparent !== "boolean") {
    errors.push("map_bg_transparent must be boolean.");
  }
  if (patch.map_legend_position != null && !["bottom", "right"].includes(patch.map_legend_position)) {
    errors.push("Legend position must be bottom or right.");
  }
  if (patch.map_logo_position != null &&
      !["top-left", "top-right", "bottom-left", "bottom-right"].includes(patch.map_logo_position)) {
    errors.push("Invalid logo position.");
  }
  if (patch.map_logo_url != null && patch.map_logo_url !== "" && !HTTP_RE.test(patch.map_logo_url)) {
    errors.push("Logo URL must be http(s).");
  }
  return errors;
}

function validateDistributor(patch) {
  const errors = [];
  if (patch.name != null && (typeof patch.name !== "string" || patch.name.length < 1 || patch.name.length > 120)) {
    errors.push("Distributor name 1–120 chars.");
  }
  if (patch.color_hex != null && !HEX_RE.test(patch.color_hex)) errors.push("Color must be #RRGGBB.");
  if (patch.website_url != null && patch.website_url !== "" && !HTTP_RE.test(patch.website_url)) {
    errors.push("Website URL must start with http(s)://.");
  }
  if (patch.logo_url != null && patch.logo_url !== "" && !HTTP_RE.test(patch.logo_url)) {
    errors.push("Logo URL must start with http(s)://.");
  }
  if (patch.show_on_map != null && typeof patch.show_on_map !== "boolean") {
    errors.push("show_on_map must be boolean.");
  }
  if (patch.sort_order != null && !Number.isInteger(patch.sort_order)) {
    errors.push("sort_order must be int.");
  }
  return errors;
}

function throwIfErrors(errors) {
  if (errors.length) throw new Error(errors.join(" "));
}

// ---------------------------------------------------------------------
// Maps
// ---------------------------------------------------------------------

// The workspace whose maps we are managing. This module predates multi-tenancy
// and used to lean entirely on RLS for scoping — see the warning on
// listMyMaps() for why that is not the same thing as scoping.
async function activeAccountId() {
  const acct = await getMyPrimaryAccount();
  if (!acct?.accountId) throw new Error("No workspace found for your account.");
  return acct.accountId;
}

// ⚠️ The account filter here is LOAD-BEARING, not belt-and-braces.
//
// The `maps` SELECT policy is deliberately an OR, not an AND:
//   owner_id = auth.uid() OR is_account_member(account_id) OR is_platform_admin()
// The owner_id arm exists so a map survives its creator leaving an account, and
// the platform-admin arm exists so staff can support a tenant. Both are correct
// as *permissions* — but they mean "what RLS returns" is a superset of "what
// this workspace owns". Listing with no filter therefore merged every map the
// caller personally created (in any workspace) and, for Brand Command staff,
// every map of every tenant, into one list — a user in two workspaces saw the
// same maps in both, and staff saw customers' maps beside their own.
//
// RLS is the security boundary; the tenant filter is the product boundary. This
// query needs both.
export async function listMyMaps() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("maps")
    .select("id, name, geography, map_type, is_published, published_at, updated_at")
    .eq("account_id", acct)
    .order("updated_at", { ascending: false });
  if (error) {
    console.error("[mapsService.listMyMaps]", error);
    throw new Error("Could not load maps.");
  }
  return data ?? [];
}

export async function createMap({ name, mapType = "distributor" }) {
  if (typeof name !== "string" || name.length < 1 || name.length > 120) {
    throw new Error("Name must be 1–120 chars.");
  }
  const type = ["region", "other"].includes(mapType) ? mapType : "distributor";
  // owner_id and geography are server/DB-controlled.
  // owner_id: the RLS check + a default would also work, but we set it
  // explicitly from auth.uid() so the WITH CHECK clause sees the right value.
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  // account_id must be sent explicitly. Leaving it null hands the row to the
  // `maps_set_account` trigger, which falls back to personal_account_for(owner)
  // — the creator's EARLIEST owner membership, not the workspace they are
  // actually in. A map made while switched to a second workspace was therefore
  // filed under the first one and then vanished from the list it was created in.
  const acct = await activeAccountId();

  const { data, error } = await supabase
    .from("maps")
    .insert({ name, owner_id: user.id, account_id: acct, geography: "us-states", map_type: type })
    .select("id")
    .single();

  if (error) {
    console.error("[mapsService.createMap]", error);
    // The insert policy enforces: active billing, entitled region, first-map-free or Pro.
    // Surface generic messages for the common failures.
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Cannot create another map on this plan. Upgrade to Pro for additional maps.");
    }
    throw new Error("Could not create map.");
  }
  return data.id;
}

export async function loadMap(mapId) {
  const [mapRes, distRes, regRes] = await Promise.all([
    supabase.from("maps").select("*").eq("id", mapId).maybeSingle(),
    supabase
      .from("map_distributors")
      .select("id, name, public_name, color_hex, website_url, logo_url, show_on_map, sort_order")
      .eq("map_id", mapId)
      .order("sort_order", { ascending: true }),
    supabase
      .from("map_regions")
      .select("id, level, region_code, distributor, active")
      .eq("map_id", mapId)
      .eq("level", "state"),
  ]);
  if (mapRes.error || distRes.error || regRes.error) {
    console.error("[mapsService.loadMap]", mapRes.error || distRes.error || regRes.error);
    throw new Error("Could not load map.");
  }
  if (!mapRes.data) throw new Error("Map not found.");
  return { map: mapRes.data, distributors: distRes.data ?? [], regions: regRes.data ?? [] };
}

const MAP_SETTINGS_ALLOWED = new Set([
  "name",
  "map_title",
  "map_title_color",
  "map_legend_text_color",
  "map_bg_color",
  "map_label_color",
  "map_unassigned_color",
  "map_bg_transparent",
  "map_legend_position",
  "map_logo_url",
  "map_logo_position",
]);

export async function updateMapSettings(mapId, patch) {
  const filtered = pick(patch, MAP_SETTINGS_ALLOWED);
  throwIfErrors(validateMapSettings(filtered));
  if (Object.keys(filtered).length === 0) return;

  const { error } = await supabase.from("maps").update(filtered).eq("id", mapId);
  if (error) {
    console.error("[mapsService.updateMapSettings]", error);
    throw new Error("Could not save settings.");
  }
}

// Delete a map. map_distributors / map_regions / map_state_modes cascade on the
// FK, so this also removes everything the map owns. Irreversible — once gone,
// any embed of this map stops resolving and fails on the host site.
export async function deleteMap(mapId) {
  const { error } = await supabase.from("maps").delete().eq("id", mapId);
  if (error) {
    console.error("[mapsService.deleteMap]", error);
    throw new Error("Could not delete map.");
  }
}

// publish/unpublish — separate from settings because published_at is touched.
export async function publishMap(mapId, publish) {
  if (typeof publish !== "boolean") throw new Error("publish must be boolean.");
  const { error } = await supabase
    .from("maps")
    .update({
      is_published: publish,
      published_at: publish ? new Date().toISOString() : null,
    })
    .eq("id", mapId);
  if (error) {
    console.error("[mapsService.publishMap]", error);
    throw new Error("Could not change publish state.");
  }
}

// ---------------------------------------------------------------------
// Distributors
// ---------------------------------------------------------------------
const DIST_ALLOWED = new Set([
  "name", "public_name", "color_hex", "website_url", "logo_url",
  "show_on_map", "sort_order",
]);

export async function createDistributor(mapId, patch) {
  const filtered = pick(patch, DIST_ALLOWED);
  throwIfErrors(validateDistributor(filtered));
  if (!filtered.name) throw new Error("Distributor name is required.");

  const { data, error } = await supabase
    .from("map_distributors")
    .insert({ ...filtered, map_id: mapId })
    .select("id")
    .single();
  if (error) {
    console.error("[mapsService.createDistributor]", error);
    if (error.code === "23505") throw new Error("A distributor with that name already exists.");
    throw new Error("Could not create distributor.");
  }
  return data.id;
}

export async function updateDistributor(distributorId, patch) {
  const filtered = pick(patch, DIST_ALLOWED);
  throwIfErrors(validateDistributor(filtered));
  if (Object.keys(filtered).length === 0) return;

  const { error } = await supabase.from("map_distributors").update(filtered).eq("id", distributorId);
  if (error) {
    console.error("[mapsService.updateDistributor]", error);
    throw new Error("Could not update distributor.");
  }
}

export async function deleteDistributor(distributorId) {
  const { error } = await supabase.from("map_distributors").delete().eq("id", distributorId);
  if (error) {
    console.error("[mapsService.deleteDistributor]", error);
    throw new Error("Could not delete distributor.");
  }
}

// Live model: distributors come from System Settings (sales_states); the only
// per-map data we store is presentation (color, label, visibility) keyed by the
// distributor NAME. Upsert on the UNIQUE(map_id, name) constraint. The caller
// passes the full desired row so insert and update stay consistent.
export async function upsertDistributorByName(mapId, name, patch) {
  if (typeof name !== "string" || !name || name.length > 120) {
    throw new Error("Invalid distributor name.");
  }
  const filtered = pick(patch, DIST_ALLOWED);
  delete filtered.name; // name is the conflict key — set explicitly below
  throwIfErrors(validateDistributor(filtered));

  const { error } = await supabase
    .from("map_distributors")
    .upsert({ map_id: mapId, name, ...filtered }, { onConflict: "map_id,name" });
  if (error) {
    console.error("[mapsService.upsertDistributorByName]", error);
    throw new Error("Could not save distributor.");
  }
}

// ---------------------------------------------------------------------
// Region <-> distributor assignment (state-level only in Phase 1)
// ---------------------------------------------------------------------
function assertStateCode(code) {
  if (typeof code !== "string" || !STATE_CODE_SET.has(code)) {
    throw new Error(`Unknown state code: ${code}`);
  }
}

export async function setRegionDistributor(mapId, stateCode, distributorName) {
  assertStateCode(stateCode);

  // Clearing: delete any existing assignment for this state on this map.
  if (!distributorName) {
    const { error } = await supabase
      .from("map_regions")
      .delete()
      .eq("map_id", mapId)
      .eq("level", "state")
      .eq("region_code", stateCode);
    if (error) {
      console.error("[mapsService.setRegionDistributor:clear]", error);
      throw new Error("Could not clear state.");
    }
    return;
  }

  if (typeof distributorName !== "string" || distributorName.length > 120) {
    throw new Error("Invalid distributor name.");
  }

  // Phase 1: one distributor per state per map. Replace any prior assignment.
  const { error: delErr } = await supabase
    .from("map_regions")
    .delete()
    .eq("map_id", mapId)
    .eq("level", "state")
    .eq("region_code", stateCode);
  if (delErr) {
    console.error("[mapsService.setRegionDistributor:replace-delete]", delErr);
    throw new Error("Could not update state.");
  }

  const { error: insErr } = await supabase
    .from("map_regions")
    .insert({
      map_id: mapId,
      level: "state",
      region_code: stateCode,
      distributor: distributorName,
      active: true,
    });
  if (insErr) {
    console.error("[mapsService.setRegionDistributor:insert]", insErr);
    throw new Error("Could not assign state.");
  }
}

// Bulk paint helper — useful when the user multi-selects then clicks a swatch.
const MAX_BULK_REGIONS = 200;
export async function setRegionDistributorBulk(mapId, stateCodes, distributorName) {
  if (!Array.isArray(stateCodes) || stateCodes.length === 0) return;
  if (stateCodes.length > MAX_BULK_REGIONS) {
    throw new Error(`Too many states in one call (max ${MAX_BULK_REGIONS}).`);
  }
  stateCodes.forEach(assertStateCode);
  for (const code of stateCodes) {
    // Serial to keep error surface simple; ~50 max in practice (50 states).
    await setRegionDistributor(mapId, code, distributorName);
  }
}
